import React, { memo } from 'react'
import { Box, Chip, Grid, RadioGroup, TableRow } from '@mui/material'
import { faCircleDot, faUpload } from '@fortawesome/pro-light-svg-icons'
import { Trans } from 'react-i18next'

import CustomDialog from '../../shared/CustomDialog/CustomDialog'
import {
  CSS_FLEX_CENTERED,
  CSS_FLEX_COLUMN,
  CSS_TRUNCATE_TEXT
} from '../../../styles/common'
import TitleCaptionText from '../../shared/text/TitleCaptionText'
import DisplayLabel from '../../shared/DisplayLabel/DisplayLabel'
import { formatToShortDate } from '../../../util/date.helpers'
import { LABEL_TEXT } from '../../../constants/messages'
import DisplayLabelWithPopover from '../../shared/DisplayLabel/DisplayLabelWithPopover'
import DataTable from '../../shared/DataTable/DataTable'
import { buildTableHeaders } from '../../../util/csv.helpers'
import { NFT_TABLE_HEADER } from '../../../constants/table'
import Text from '../../shared/text/Text'
import FaIcon from '../../shared/FaIcon/FaIcon'
import { formatTicks, toUpperFirstLetter } from '../../../util/string.helpers'
import { NFT_SUPPLY_MAX } from '../../../constants/validation'
import TableCellData from '../../shared/DataTable/TableCellData'
import StatusLabel from '../../shared/StatusLabel/StatusLabel'
import SmallButton from '../../shared/buttons/SmallButton'
import {
  buildTokenChipColor,
  formatPointValue
} from '../../../util/token.helpers'
import RadioButton from '../../shared/RadioButton/RadioButton'
import {
  buildStatusLabelColor,
  convertToYesNo
} from '../../../util/nft.helpers'
import Link from '../../shared/link/Link'
import { MARKDOWN_URL } from '../../../constants/externalResources'
import TitleWithPopover from '../../shared/TitleWithPopover/TitleWithPopover'
import NftDetailsMedia from '../../nft/NftDetailsMedia'
import { prependHttpsIfNotExists } from '../../../util/app/app.helpers'
import { formatToDecimals } from '../../../util/number.helpers'
import Loading from '../../shared/Loading/Loading'

const NftDetails = ({
  onClose,
  onSubmit,
  nft,
  tokenProfile: { name: tokenName, symbol, onramp: tokenOnramp },
  brandName,
  onShowNftChange,
  showNft,
  nftUsersProfile,
  onChangeNftUsersPage,
  onNftUsersPerPageChange,
  onRedeemNftExclusiveContent,
  t
}) => {
  const {
    id,
    name,
    metadata,
    nftType,
    supply,
    createdAt,
    status,
    onramp,
    canSell,
    canTransfer,
    salesFeePercentage
  } = nft
  const {
    media,
    exclusiveContent,
    externalLink,
    description: { data: descriptionData },
    legacy
  } = metadata
  const { users, pagination, isProcessing } = nftUsersProfile
  const { pageNumber, recordsPerPage, totalTxCount } = pagination
  const pointValue = onramp / tokenOnramp
  const nftTableHeaders = buildTableHeaders(t, NFT_TABLE_HEADER)
  return (
    <CustomDialog
      open
      onClose={onClose}
      onSubmit={onSubmit}
      submitProps={{
        startIcon: <FaIcon icon={faUpload} width={18} />,
        label: t('nft.transfer-nft-to-member')
      }}
      actionStyles={{ pt: 0, p: '20px' }}
      paperProps={{ maxWidth: '650px' }}
      t={t}
      additionalActions={
        <Box sx={CSS_FLEX_CENTERED}>
          <TitleWithPopover
            fontWeight={700}
            variant='caption'
            component='div'
            title={t('nft.your-supply')}
            helpText={t('nft.supply-desc-users', { brandName })}
          />
          <Text
            label={`: ${formatTicks(supply - totalTxCount)} / ${formatTicks(
              supply
            )}`}
            variant='caption'
            component='div'
            sx={{ maxHeight: 96, overflow: 'auto', lineHeight: 1.3 }}
          />
        </Box>
      }
      banner={
        <Grid
          container
          sx={{ ...CSS_FLEX_COLUMN, p: '20px', width: '100%', m: 0 }}
        >
          <Grid item sx={{ display: 'flex', width: '100%', m: 0 }}>
            <Grid item xs={4.5}>
              <NftDetailsMedia
                media={media}
                coverImage={legacy?.coverImage}
                sx={{
                  borderRadius: '7px'
                }}
              />
            </Grid>
            <Grid
              item
              xs={7.5}
              sx={{
                ...CSS_FLEX_COLUMN,
                justifyContent: 'space-between',
                pl: '20px'
              }}
            >
              <Grid
                container
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexWrap: 'nowrap'
                }}
              >
                <Grid item>
                  <TitleCaptionText
                    title={name}
                    caption={brandName}
                    titleStyles={{ whiteSpace: 'normal' }}
                    sx={{ mb: 0 }}
                  />
                </Grid>
                <Grid item>
                  <Chip
                    label={`${t('common.status')}: ${t(
                      `nft.nft-status-types.${status}`
                    )}`}
                    size='small'
                    color={buildTokenChipColor(status)}
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={4}>
                  <DisplayLabelWithPopover
                    label={t(LABEL_TEXT.TYPE)}
                    helpText={t('nft.type-desc')}
                    value={toUpperFirstLetter(nftType)}
                  />
                </Grid>
                <Grid item xs={4}>
                  <DisplayLabelWithPopover
                    label={t('nft.supply')}
                    helpText={t('nft.supply-desc')}
                    value={`${formatTicks(supply)} / ${formatTicks(
                      NFT_SUPPLY_MAX
                    )}`}
                  />
                </Grid>
                <Grid item xs={4}>
                  <DisplayLabel
                    label={t('nft.creation-date')}
                    value={formatToShortDate(createdAt)}
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={4}>
                  <DisplayLabelWithPopover
                    label={t('nft.user-sale')}
                    helpText={t('nft.user-sale-desc')}
                    value={`${convertToYesNo(canSell, t)} ${
                      salesFeePercentage
                        ? `(${formatToDecimals(
                            Number(salesFeePercentage),
                            2
                          )}%)`
                        : ''
                    }`}
                  />
                </Grid>
                <Grid item xs={4}>
                  <DisplayLabelWithPopover
                    label={t('nft.user-transfer')}
                    helpText={t('nft.user-transfer-desc')}
                    value={convertToYesNo(canTransfer, t)}
                  />
                </Grid>
                <Grid item xs={4}>
                  <DisplayLabel
                    label={t('common.blockchain')}
                    value={t('nft.qiibee-rewards-chain')}
                  />
                </Grid>
              </Grid>
              <Grid container>
                {pointValue > 0 && (
                  <Grid item xs={6}>
                    <DisplayLabelWithPopover
                      label={t('nft.point-value')}
                      helpText={t('nft.point-value-desc', {
                        symbol,
                        name: tokenName
                      })}
                      value={formatPointValue(pointValue)}
                    />
                  </Grid>
                )}
                {externalLink && (
                  <Grid item xs={6}>
                    <DisplayLabelWithPopover
                      label={t('nft.external-link')}
                      helpText={t('nft.external-link-desc')}
                      value={
                        <Link
                          isOutside
                          label={externalLink}
                          to={prependHttpsIfNotExists(externalLink)}
                          sx={{
                            width: '100%',
                            ...CSS_TRUNCATE_TEXT
                          }}
                        />
                      }
                    />
                  </Grid>
                )}
              </Grid>
              <Grid container>
                <Grid
                  item
                  sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}
                >
                  <DisplayLabelWithPopover
                    label={t('nft.show-nft')}
                    helpText={t('nft.show-nft-desc')}
                  />
                  <RadioGroup
                    row
                    value={showNft}
                    onChange={(event) => onShowNftChange(id, event)}
                  >
                    <RadioButton
                      value='true'
                      label={t('nft.yes-no-options.yes')}
                    />
                    <RadioButton
                      value='false'
                      label={t('nft.yes-no-options.no')}
                    />
                  </RadioGroup>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item sx={{ ...CSS_FLEX_COLUMN, width: '100%', m: 0 }}>
            {descriptionData && (
              <Grid item sx={{ pt: '20px' }}>
                <DisplayLabelWithPopover
                  label={t('common.description')}
                  helpText={
                    <Trans i18nKey='nft.description-desc'>
                      <Link
                        isOutside
                        label={t('common.markdown')}
                        to={MARKDOWN_URL}
                      />
                    </Trans>
                  }
                  value={descriptionData}
                />
              </Grid>
            )}
            {exclusiveContent && (
              <Grid item sx={{ pt: '20px' }}>
                <DisplayLabelWithPopover
                  label={t('nft.exclusive-content')}
                  helpText={
                    <Trans i18nKey='nft.exclusive-content-desc'>
                      <Link
                        isOutside
                        label={t('common.markdown')}
                        to={MARKDOWN_URL}
                      />
                    </Trans>
                  }
                  value={exclusiveContent}
                />
              </Grid>
            )}
            <Grid item sx={{ pt: '20px', ...CSS_FLEX_COLUMN }}>
              <Text
                label={t('nft.nfts-owned-by-members')}
                variant='caption'
                fontWeight={700}
              />
              {isProcessing ? (
                <Loading noFullHeight />
              ) : users.length ? (
                <DataTable
                  headers={nftTableHeaders}
                  data={users?.map((item, index) => (
                    <DataTableRow
                      key={index}
                      number={pageNumber * recordsPerPage + index + 1}
                      {...item}
                      t={t}
                      tokenId={id}
                      exclusiveContent={exclusiveContent}
                      onRedeemNftExclusiveContent={onRedeemNftExclusiveContent}
                    />
                  ))}
                  pagination={pagination}
                  onChangePage={onChangeNftUsersPage}
                  onRowsPerPageChange={onNftUsersPerPageChange}
                  t={t}
                  sx={{
                    minWidth: 0
                  }}
                  paginationProps={{
                    '&.MuiTablePagination-root, .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows, .MuiSvgIcon-root':
                      {
                        fontSize: '0.6rem'
                      }
                  }}
                />
              ) : (
                <Text
                  label={t('nft.no-member-owns-this-nft')}
                  variant='caption'
                  component='div'
                  sx={{ maxHeight: 96, overflow: 'auto', lineHeight: 1.3 }}
                />
              )}
            </Grid>
          </Grid>
        </Grid>
      }
    />
  )
}

export default memo(NftDetails)

const DataTableRow = ({
  number,
  user,
  t,
  claimCount,
  exclusiveContent,
  tokenId,
  nftId,
  onRedeemNftExclusiveContent
}) => {
  const { authId, firstName, secondName, email } = user
  const { label, color } = buildStatusLabelColor(exclusiveContent, claimCount)
  return (
    <TableRow>
      <TableCellData sx={{ p: 0 }}>
        <Box fontSize={10}>{number}</Box>
      </TableCellData>
      <TableCellData sx={{ p: 0 }}>
        <Box sx={{ pl: 2, ...CSS_FLEX_COLUMN }}>
          <Box fontSize={10} fontWeight={600}>
            {authId}
          </Box>
          <Box fontSize={10} sx={{ color: '', fontWeight: 500 }}>
            {firstName} {secondName} - {email}
          </Box>
        </Box>
      </TableCellData>
      <TableCellData noTag sx={{ p: 0 }}>
        <StatusLabel
          label={t(`nft.excl-cnt-status-label.${label}`)}
          color={color}
          sx={{ fontSize: '10px' }}
        />
      </TableCellData>
      <TableCellData sx={{ p: 0 }}>
        {exclusiveContent && (
          <SmallButton
            onClick={() =>
              onRedeemNftExclusiveContent(authId, tokenId, nftId, claimCount)
            }
            label={t('nft.change-status')}
            startIcon={<FaIcon width={10} icon={faCircleDot} />}
            size='small'
            sx={{ fontSize: '10px', p: 0, pl: 0.8, pr: 0.8, borderRadius: 0 }}
          />
        )}
      </TableCellData>
    </TableRow>
  )
}
